import { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import { RedemptionInfo } from "@/utils/extractRedemptionInfo";
import { useResendRedemption } from "@/api";

export interface ResendData {
  companyId: string;
  userId: string;
  cartId: string;
  utid: string;
  productIndex: number;
  originalEmail: string;
}

interface RedemptionSectionProps {
  redemptionInfo: RedemptionInfo;
  resendData?: ResendData;
}

export const RedemptionSection = ({
  redemptionInfo,
  resendData,
}: RedemptionSectionProps) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  // Resend modal state
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  const [resendRedemption, , resendLoading] = useResendRedemption();

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleOpenResendModal = () => {
    setResendEmail(resendData?.originalEmail || "");
    setResendSuccess(null);
    setResendError(null);
    setResendModalOpen(true);
  };

  const handleCloseResendModal = () => {
    setResendModalOpen(false);
    setResendSuccess(null);
    setResendError(null);
  };

  const handleResend = async () => {
    if (!resendData || !resendEmail) return;

    setResendError(null);
    setResendSuccess(null);

    try {
      const result = await resendRedemption({
        companyId: resendData.companyId,
        userId: resendData.userId,
        cartId: resendData.cartId,
        utid: resendData.utid,
        productIndex: resendData.productIndex,
        email: resendEmail,
      });

      setResendSuccess(result.message);
    } catch (error) {
      setResendError(
        error instanceof Error
          ? error.message
          : "Failed to resend redemption email",
      );
    }
  };

  const {
    hasDirectRedemption,
    hasSecurityCodeFlow,
    finalRedemptionUrl,
    displayCode,
    redemptionUrl,
    rewardSecurityUrl,
    redemptionCode,
    redemptionInstructions,
  } = redemptionInfo;

  // Replace "redemption URL" with "redemption link" in instructions, preserving capitalization
  const sanitizedRedemptionInstructions = redemptionInstructions?.replace(
    /redemption url/gi,
    (match) => {
      if (match === "Redemption URL") return "Redemption Link";
      if (match === "redemption URL") return "redemption Link";
      if (match === "REDEMPTION URL") return "REDEMPTION LINK";
      return "redemption link";
    },
  );

  // Check if there's any redemption information to show
  const hasRedemptionInfo =
    hasDirectRedemption ||
    hasSecurityCodeFlow ||
    finalRedemptionUrl ||
    redemptionInstructions;

  // Don't show anything if there's no redemption info AND no resend data
  if (!hasRedemptionInfo && !resendData) {
    return null;
  }

  return (
    <>
      {hasRedemptionInfo && (
        <Accordion
          sx={{
            mt: 2,
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            "&:before": {
              display: "none",
            },
          }}
          data-cypress="redemption-section"
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#f9f9f9",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
            data-cypress="redemption-section-header"
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: primaryColor,
              }}
            >
              Click Here for Redemption Instructions
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              pt: 2,
              pb: 2,
            }}
            data-cypress="redemption-section-content"
          >
            <Box>
              {/* Display code prominently */}
              {displayCode && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    background: `linear-gradient(135deg, ${alpha(primaryColor, 0.08)} 0%, ${alpha(primaryColor, 0.03)} 100%)`,
                    border: `2px solid ${primaryColor}`,
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                  data-cypress="redemption-code-container"
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: primaryColor,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Your Redemption Code
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "'Courier New', Courier, monospace",
                        fontWeight: 700,
                        color: "#000",
                        letterSpacing: 2,
                        wordBreak: "break-all",
                      }}
                      data-cypress="redemption-code"
                    >
                      {displayCode}
                    </Typography>
                    <Tooltip title={codeCopied ? "Copied!" : "Copy code"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(displayCode)}
                        sx={{
                          color: codeCopied ? "success.main" : primaryColor,
                          "&:hover": {
                            backgroundColor: alpha(primaryColor, 0.1),
                          },
                        }}
                        data-cypress="copy-code-button"
                      >
                        {codeCopied ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {/* Redemption Instructions */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: primaryColor,
                    mb: 1,
                  }}
                >
                  To Redeem
                </Typography>
                <Box
                  sx={{
                    fontSize: "14px",
                    color: "#000",
                    lineHeight: 1.6,
                    backgroundColor: "#f9f9f9",
                    padding: 2,
                    borderRadius: 1,
                  }}
                >
                  {hasDirectRedemption && redemptionInstructions ? (
                    <>
                      {/* Direct redemption flow with product instructions (Lowe's type) */}
                      {redemptionUrl && (
                        <Box sx={{ mb: 1.5 }}>
                          <Button
                            variant="contained"
                            endIcon={<OpenInNewIcon />}
                            href={redemptionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-cypress="redemption-url"
                          >
                            Redeem Gift Card
                          </Button>
                        </Box>
                      )}
                      <Box
                        sx={{ mt: 1.5 }}
                        dangerouslySetInnerHTML={{
                          __html: sanitizedRedemptionInstructions || "",
                        }}
                        data-cypress="redemption-instructions"
                      />
                    </>
                  ) : hasDirectRedemption && redemptionUrl ? (
                    /* Direct redemption flow without product instructions */
                    <Box sx={{ mb: 1.5 }}>
                      <Button
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        href={redemptionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-cypress="redemption-url"
                      >
                        Redeem Gift Card
                      </Button>
                    </Box>
                  ) : hasSecurityCodeFlow ? (
                    /* Security code flow (Walmart/Amazon type) */
                    <>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        To redeem your reward, please follow these steps:
                      </Typography>
                      <Box component="ol" sx={{ pl: 2.5, mb: 1.5 }}>
                        <li style={{ marginBottom: "10px" }}>
                          <Typography variant="body2" component="span">
                            Copy security code:{" "}
                            <strong>{redemptionCode}</strong>
                          </Typography>
                        </li>
                        <li style={{ marginBottom: "10px" }}>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            Click the button below and paste the security code
                            to unlock the reward.
                            <br />
                            <br />
                            Please note that this is not the same code used for
                            your purchase.
                          </Typography>
                          {rewardSecurityUrl && (
                            <Button
                              variant="contained"
                              endIcon={<OpenInNewIcon />}
                              href={rewardSecurityUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-cypress="redemption-security-url"
                            >
                              Unlock Reward
                            </Button>
                          )}
                        </li>
                        <li style={{ marginBottom: "10px" }}>
                          <Typography variant="body2">
                            Once unlocked, the reward code will be revealed.
                          </Typography>
                        </li>
                      </Box>
                    </>
                  ) : finalRedemptionUrl ? (
                    /* Fallback: just show URL if available */
                    <Box sx={{ mb: 1.5 }}>
                      <Button
                        variant="contained"
                        endIcon={<OpenInNewIcon />}
                        href={finalRedemptionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-cypress="redemption-url"
                      >
                        Redeem Gift Card
                      </Button>
                    </Box>
                  ) : redemptionInstructions ? (
                    /* Show instructions if available */
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: sanitizedRedemptionInstructions || "",
                      }}
                      data-cypress="redemption-instructions"
                    />
                  ) : (
                    /* No redemption info available */
                    <Typography variant="body2">
                      Here you will find redemption instructions for the eGift
                      card. The instructions will vary between brand.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Resend Redemption Button - Only shown in super admin context */}
      {resendData && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={handleOpenResendModal}
            size="small"
            data-cypress="resend-redemption-button"
          >
            Resend Redemption
          </Button>
        </Box>
      )}

      {/* Resend Redemption Modal */}
      <Dialog
        open={resendModalOpen}
        onClose={handleCloseResendModal}
        maxWidth="sm"
        fullWidth
        data-cypress="resend-redemption-modal"
      >
        <DialogTitle>Resend Redemption Email</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {resendSuccess && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                data-cypress="resend-success"
              >
                {resendSuccess}
              </Alert>
            )}
            {resendError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                data-cypress="resend-error"
              >
                {resendError}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label="Email Address"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              disabled={resendLoading || !!resendSuccess}
              data-cypress="resend-email-input"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block" }}
            >
              The redemption email will be sent to this address with the same
              gift card details.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResendModal} disabled={resendLoading}>
            {resendSuccess ? "Close" : "Cancel"}
          </Button>
          {!resendSuccess && (
            <Button
              onClick={handleResend}
              variant="contained"
              disabled={resendLoading || !resendEmail}
              startIcon={
                resendLoading ? <CircularProgress size={16} /> : <SendIcon />
              }
              data-cypress="resend-confirm-button"
            >
              {resendLoading ? "Sending..." : "Resend"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
